import {apiSlice} from './apiSlice';

/**
 * Endpoints tool Nghiên cứu từ khoá (#5, bên trong một Project — docs/ke-hoach-keyword-research.md).
 *
 * - explore: kho query GSC của dự án (đọc TỪ DB, impressions thật thay "search volume") +
 *   bộ lọc q/min_impr/pos_min/pos_max. connected=false khi dự án chưa kết nối GSC → FE hiện CTA.
 * - opportunities: 3 khối cơ hội (gần trang 1 / CTR thấp bất thường / cụm chủ đề).
 * - generateKeywordIdeas: AI xây cụm ý tưởng (tốn token AI → invalidate 'Subscription';
 *   lưu ai_history nên invalidate 'AiHistory' để tab Lịch sử AI của dự án tự cập nhật).
 * - items: kho từ khoá đã lưu (CRUD, trần theo gói max_keyword_items).
 * - analyze-page / page-terms / gap: Phân tích trang & Content gap (gate cờ gói
 *   can_keyword_gap; URL đối thủ tiêu 1 lượt url_checks → invalidate 'Subscription').
 *
 * Tag: 'Keyword' (explore/opportunities/pages, để cập nhật cờ saved) + 'KeywordItem' (kho).
 */
export const keywordApiSlice = apiSlice.injectEndpoints({
	endpoints: (builder) => ({
		getKeywordExplore: builder.query({
			query: ({projectId, q, min_impr, pos_min, pos_max, from, to}) => ({
				url: `keywords/projects/${projectId}/explore`,
				method: 'get',
				params: {
					...(q ? {q} : {}),
					...(min_impr ? {min_impr} : {}),
					...(pos_min ? {pos_min} : {}),
					...(pos_max ? {pos_max} : {}),
					...(from ? {from} : {}),
					...(to ? {to} : {}),
				},
			}),
			transformResponse: (body) => body?.data || {connected: false, rows: []},
			providesTags: (result, error, {projectId}) => [{type: 'Keyword', id: `explore-${projectId}`}],
		}),
		getKeywordOpportunities: builder.query({
			query: ({projectId, from, to}) => ({
				url: `keywords/projects/${projectId}/opportunities`,
				method: 'get',
				...(from || to ? {params: {...(from ? {from} : {}), ...(to ? {to} : {})}} : {}),
			}),
			transformResponse: (body) => body?.data || {connected: false},
			providesTags: (result, error, {projectId}) => [{type: 'Keyword', id: `opportunities-${projectId}`}],
		}),
		// Content gap CẤP SITE (#28): thiếu bài viết + thiếu internal link (GSC × crawl, thuần DB).
		getKeywordSiteGap: builder.query({
			query: (projectId) => ({url: `keywords/projects/${projectId}/site-gap`, method: 'get'}),
			transformResponse: (body) => body?.data || {connected: false},
			providesTags: (result, error, projectId) => [{type: 'Keyword', id: `site-gap-${projectId}`}],
		}),
		generateKeywordIdeas: builder.mutation({
			query: ({projectId, seed, context}) => ({
				url: `keywords/projects/${projectId}/ideas`,
				method: 'post',
				data: {seed, ...(context ? {context} : {})},
			}),
			transformResponse: (body) => body?.data || null,
			// 'Subscription' → sidebar token còn lại tự cập nhật sau khi tốn token.
			invalidatesTags: ['AiHistory', 'Subscription'],
		}),
		// Gợi ý từ khoá Google Keyword Planner (LIVE): seed keywords/URL → ideas kèm volume/CPC
		// THẬT (tài khoản test → số demo). Tốn 1 lượt tra volume/request → invalidate 'Subscription'.
		googleKeywordIdeas: builder.mutation({
			query: ({projectId, keywords, url}) => ({
				url: `keywords/projects/${projectId}/google-ideas`,
				method: 'post',
				data: {...(keywords?.length ? {keywords} : {}), ...(url ? {url} : {})},
			}),
			transformResponse: (body) => body?.data || null,
			invalidatesTags: ['Subscription'],
		}),
		// Danh sách trang đã phân tích (không kèm terms — chi tiết lấy qua analyzeKeywordPage, cache 24h phía BE).
		getKeywordPageTerms: builder.query({
			query: (projectId) => ({url: `keywords/projects/${projectId}/page-terms`, method: 'get'}),
			transformResponse: (body) => body?.data?.rows || [],
			providesTags: (result, error, projectId) => [{type: 'Keyword', id: `pages-${projectId}`}],
		}),
		analyzeKeywordPage: builder.mutation({
			query: ({projectId, url, refresh}) => ({
				url: `keywords/projects/${projectId}/analyze-page`,
				method: 'post',
				data: {url, ...(refresh ? {refresh: 1} : {})},
			}),
			transformResponse: (body) => body?.data || null,
			// URL đối thủ trừ 1 lượt url_checks → 'Subscription' cập nhật đồng hồ hạn mức.
			invalidatesTags: (result, error, {projectId}) => [
				{type: 'Keyword', id: `pages-${projectId}`},
				'Subscription',
			],
		}),
		deleteKeywordPageTerm: builder.mutation({
			query: ({projectId, id}) => ({url: `keywords/projects/${projectId}/page-terms/${id}`, method: 'delete'}),
			invalidatesTags: (result, error, {projectId}) => [{type: 'Keyword', id: `pages-${projectId}`}],
		}),
		keywordGap: builder.mutation({
			query: ({projectId, base_id, rival_id}) => ({
				url: `keywords/projects/${projectId}/gap`,
				method: 'post',
				data: {base_id, rival_id},
			}),
			transformResponse: (body) => body?.data || null,
		}),
		// Volume/CPC/KD qua DataForSEO (§8): đọc cache chung trước (0đ), miss mới trừ quota
		// tháng + xếp hàng gộp lô. IDEMPOTENT — FE poll bằng chính mutation này khi còn
		// dòng status 'pending' (gọi lại không enqueue thêm/không trừ quota thêm).
		lookupKeywordMetrics: builder.mutation({
			query: ({projectId, keywords}) => ({
				url: `keywords/projects/${projectId}/metrics`,
				method: 'post',
				data: {keywords},
			}),
			transformResponse: (body) => body?.data || null,
		}),
		getKeywordItems: builder.query({
			query: (projectId) => ({url: `keywords/projects/${projectId}/items`, method: 'get'}),
			transformResponse: (body) => body?.data || {items: [], used: 0, max: 0},
			providesTags: (result, error, projectId) => [{type: 'KeywordItem', id: `project-${projectId}`}],
		}),
		// Thêm từ khoá vào kho: items = [{keyword, source?, intent?, note?, impressions?, position?, meta?}].
		// Invalidate cả explore để cập nhật cờ `saved` trên bảng Khám phá.
		addKeywordItems: builder.mutation({
			query: ({projectId, items, keyword}) => ({
				url: `keywords/projects/${projectId}/items`,
				method: 'post',
				data: items ? {items} : {keyword},
			}),
			transformResponse: (body) => body?.data || null,
			invalidatesTags: (result, error, {projectId}) => [
				{type: 'KeywordItem', id: `project-${projectId}`},
				{type: 'Keyword', id: `explore-${projectId}`},
				{type: 'Keyword', id: `opportunities-${projectId}`},
			],
		}),
		deleteKeywordItem: builder.mutation({
			query: ({projectId, id}) => ({url: `keywords/projects/${projectId}/items/${id}`, method: 'delete'}),
			// Xoá item đang tracked cũng làm mất nó khỏi Theo dõi thứ hạng (1 kho duy nhất)
			// → invalidate cả GscKeyword để tab Rank Tracking refetch.
			invalidatesTags: (result, error, {projectId}) => [
				{type: 'KeywordItem', id: `project-${projectId}`},
				{type: 'Keyword', id: `explore-${projectId}`},
				{type: 'Keyword', id: `opportunities-${projectId}`},
				{type: 'GscKeyword', id: `project-${projectId}`},
			],
		}),
	}),
});

export const {
	useGetKeywordExploreQuery,
	useGetKeywordOpportunitiesQuery,
	useGetKeywordSiteGapQuery,
	useGenerateKeywordIdeasMutation,
	useGoogleKeywordIdeasMutation,
	useLookupKeywordMetricsMutation,
	useGetKeywordPageTermsQuery,
	useAnalyzeKeywordPageMutation,
	useDeleteKeywordPageTermMutation,
	useKeywordGapMutation,
	useGetKeywordItemsQuery,
	useAddKeywordItemsMutation,
	useDeleteKeywordItemMutation,
} = keywordApiSlice;
